import React from 'react'
import { Box, Group, Loader, Stack, Text } from '@mantine/core'

/** Minimal comment shape for one-level reply threads (Slop, Posts, etc.). */
export interface PlatformCommentNode {
  id: number;
  body: string;
  authorUsername?: string | null;
  parentCommentId?: number | null;
  replies?: PlatformCommentNode[];
  createdAt: string;
}

export interface OneLevelCommentListProps {
  comments: PlatformCommentNode[];
  loading?: boolean;
  emptyMessage?: string;
  formatCreatedAt?: (iso: string) => string;
  renderActions?: (comment: PlatformCommentNode, depth: number) => React.ReactNode;
}

const defaultFormat = (iso: string) => {
  try {
    return new Date(iso).toLocaleString()
  } catch {
    return iso
  }
}

/** Presentational list: top-level comments with nested replies (depth 1). */
export function OneLevelCommentList ({
  comments,
  loading = false,
  emptyMessage = 'No comments yet.',
  formatCreatedAt = defaultFormat,
  renderActions
}: OneLevelCommentListProps) {
  if (loading) {
    return (
      <Group position="center" py="md">
        <Loader size="sm" aria-label="Loading comments" />
      </Group>
    )
  }

  const topLevel = comments.filter((c) => c.parentCommentId == null)

  if (topLevel.length === 0) {
    return (
      <Text size="sm" color="dimmed" py="sm">
        {emptyMessage}
      </Text>
    )
  }

  const renderNode = (c: PlatformCommentNode, depth: number) => (
    <Box
      key={c.id}
      mb="sm"
      pl={depth > 0 ? 12 : 0}
      sx={{
        borderLeft: depth > 0 ? '2px solid var(--gs-border, #333)' : undefined
      }}
    >
      <Text size="sm" weight={500} sx={{ color: 'var(--gs-text, inherit)' }}>
        {c.authorUsername || 'User'}
      </Text>
      <Text size="xs" color="dimmed">
        {formatCreatedAt(c.createdAt)}
      </Text>
      <Text size="sm" mt={4} sx={{ color: 'var(--gs-text, inherit)' }}>
        {c.body}
      </Text>
      {renderActions?.(c, depth)}
      {(c.replies ?? []).map((r) => renderNode(r, 1))}
    </Box>
  )

  return <Stack spacing="xs">{topLevel.map((c) => renderNode(c, 0))}</Stack>
}
